import React from "react"
import { useStateMachine } from "../../context/stateMachine/StateMachineV2"
import { useReimbursementContext } from "../../context/reimbursement/GetReimbursedContext"
import { Progress } from "../../views/modal/Progress"
import { ArrowSubmitButton, ReimbursementDialogFraming, BackArrow } from "../shared/layouts"
import { IVFCard } from "./modules/IvfCard"
import { DoulaRequirementsCard } from "./modules/DoulaRequirementsCard"
import CarrotRxMedicationCard from "./modules/CarrotRxMedicationCard"
import WhatKindOfJourneyEnums from "../../../utils/WhatKindOfJourneyEnums"
import useBenefit from "../../dynamic-content/hooks/useGetBenefit"
import { isPelvicFloorSubtype, JourneySubTypes, JourneyTypes } from "./ExpenseJourneyTypes"
import { PelvicFloorRequirementsCard } from "./modules/PelvicFloorRequirementsCard"
import { FormattedMessage, useIntl } from "react-intl"
import { Box, DialogContentText, DialogTitle } from "@carrotfertility/carotene-core"
import {
  DialogActionsBar,
  Form,
  FormOnSubmitHandler,
  FormRadioGroup,
  FormSelect,
  useWatch
} from "@carrotfertility/carotene-core-x"

const JourneyModule = (props: { hasRequirements: boolean }) => {
  const { data: benefit } = useBenefit()
  const kindOfJourney = useWatch({ name: "kindOfJourney" })
  const isDoulaService = kindOfJourney ? kindOfJourney.includes("DOULA") : false

  if (isPelvicFloorSubtype(kindOfJourney)) {
    return <PelvicFloorRequirementsCard />
  }

  if (props.hasRequirements && kindOfJourney === WhatKindOfJourneyEnums.IVF) {
    return <IVFCard />
  }

  if (isDoulaService) return <DoulaRequirementsCard />

  if (kindOfJourney === JourneySubTypes.MEDICATION && benefit?.carrotRx?.isCarrotRxRequired) {
    return <CarrotRxMedicationCard />
  }

  return null
}

export const AboutThisExpenseWhatKindOfJourney = ({
  headerText,
  bodyText,
  selectOptions,
  hasRequirements = false
}: {
  headerText: string
  bodyText: string
  selectOptions: { label: string; value: string }[]
  hasRequirements?: boolean
}): JSX.Element => {
  const { send, back } = useStateMachine()
  const { updateWhatKindOfJourney, updateJourneySubtype, state } = useReimbursementContext()
  const { kindOfJourney, journeySubtype } = state
  const { data: benefit } = useBenefit()

  const intl = useIntl()

  const shouldUseSubtype = [
    JourneyTypes.PREGNANCY_AND_POSTPARTUM_SERVICES,
    JourneyTypes.MENOPAUSE,
    JourneyTypes.LOW_TESTOSTERONE,
    JourneyTypes.GENDER_AFFIRMING_CARE
    // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
  ].includes(JourneyTypes[state.expenseRelatedTo.value])

  const setDefault = (selection: { label: string; value: string }) => {
    return selection?.label ? selection.value : ""
  }

  const handleOnSubmit: FormOnSubmitHandler<{ kindOfJourney: string }> = (values) => {
    const answerAsSelectOption = selectOptions.find((j) => j.value === values.kindOfJourney)

    if (isPelvicFloorSubtype(values.kindOfJourney)) {
      updateJourneySubtype(answerAsSelectOption)
      send("ADDITIONAL_INFO_ANYTHING_ELSE")
      return
    }

    let nextStep = ""

    if (shouldUseSubtype) {
      updateJourneySubtype(answerAsSelectOption)
      // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      switch (JourneySubTypes[answerAsSelectOption.value]) {
        case JourneySubTypes.MEDICATION:
        case JourneySubTypes.IN_PERSON_CARE:
        case JourneySubTypes.ONLINE_CARE:
        case JourneySubTypes.NUTRITION_COUNSELING:
        case JourneySubTypes.TEMPERATURE_REGULATION_PRODUCTS:
        case JourneySubTypes.LABS:
        case JourneySubTypes.TRAVEL_ELIGIBLE_PROVIDER:
        case JourneySubTypes.MEDICAL_APPOINTMENTS:
        case JourneySubTypes.METABOLIC_FERTILITY_HEALTH:
          nextStep = "ATTESTATION_MEDICALLY_NECESSARY_AND_ELIGIBLE"
          break
        case JourneySubTypes.BINDERS_PACKERS_BODY_SHAPING:
          nextStep = "ATTESTATION_MEDICALLY_RELATED"
          break
      }
    } else {
      if (
        benefit.journeys.fertilityCare.eligibleExpensesDetail.obGynProvidedFertilityCareCovered &&
        [JourneyTypes.IUI.toString(), JourneyTypes.PATHFINDING.toString()].includes(answerAsSelectOption.value)
      ) {
        nextStep = "FERTILITY_CARE_PROVIDER"
      }
      updateWhatKindOfJourney(answerAsSelectOption)
    }

    send(nextStep)
  }

  return (
    <Form
      onSubmit={handleOnSubmit}
      defaultValues={{ kindOfJourney: shouldUseSubtype ? setDefault(journeySubtype) : setDefault(kindOfJourney) }}
    >
      <Progress
        numSteps={3}
        step={2}
        label={intl.formatMessage({ defaultMessage: "About This Expense" })}
        inactiveStepBackgroundColor="#DBE6EE"
      />
      <BackArrow onClick={back} />
      <ReimbursementDialogFraming>
        <DialogTitle id="step-heading">{headerText}</DialogTitle>
        <Box sx={{ display: "flex", flexDirection: "column", gap: (theme) => theme.spacing(theme.tokens.spacing.lg) }}>
          <DialogContentText>{bodyText}</DialogContentText>
          {selectOptions.length <= 3 ? (
            <FormRadioGroup
              name="kindOfJourney"
              aria-labelledby="step-heading"
              controllerProps={{
                rules: {
                  required: intl.formatMessage({ defaultMessage: "Required" })
                }
              }}
              options={selectOptions}
            />
          ) : (
            <FormSelect
              name="kindOfJourney"
              aria-labelledby="step-heading"
              controllerProps={{
                rules: {
                  required: intl.formatMessage({ defaultMessage: "Required" })
                }
              }}
              options={selectOptions}
              label={intl.formatMessage({ defaultMessage: "Select option" })}
            />
          )}
          <JourneyModule hasRequirements={hasRequirements} />
        </Box>
      </ReimbursementDialogFraming>
      <DialogActionsBar
        primaryAction={
          <ArrowSubmitButton>
            <FormattedMessage defaultMessage="Continue" />
          </ArrowSubmitButton>
        }
      />
    </Form>
  )
}
